import { FC, useEffect, useRef, useCallback, useState } from 'react';
import { Claim } from '@scale/shared/types/queue';
import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TaskHeader, Tooltip } from '@scale/scaleui';
import { toast } from 'react-toastify';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import ToolTipText from 'react-scale/components/Expert/Earnings/components/TooltipText';
import { getRemainingTimeWarningThresholdMinutes } from './utils';
import { formatTime, TIME_FORMAT_PRESETS } from '@scale/shared/utils/dateUtils';

type InfoDeadlineProps = {
  claim?: Claim;
  // todo: remove once new header fully rolled out
  visible?: boolean;
  newHeader?: boolean;
  projectId?: string;
  assignment?: AssignmentJson;
  taskId?: string;
};

export const InfoDeadline: FC<InfoDeadlineProps> = ({
  claim,
  visible,
  newHeader,
  projectId,
  assignment,
  taskId,
}) => {
  const { maxClaim: maxClaimDate, claimMinutes } = useClaimTime(
    claim,
    projectId,
    assignment,
    taskId,
  );

  let deadlineStr = new Intl.DateTimeFormat('default', {
    weekday: 'long',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  }).format(maxClaimDate);

  if (newHeader) {
    deadlineStr = new Intl.DateTimeFormat('default', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h12',
    }).format(maxClaimDate);
  }

  const remainingDeadlineSeconds = Math.max(
    0,
    (new Date(maxClaimDate).valueOf() - new Date().valueOf()) / 1000,
  );

  // time left in milliseconds
  const [timeLeftMs, setTimeLeftMs] = useState(
    Math.max(0, maxClaimDate.valueOf() - new Date().valueOf()),
  );
  const timeLeftMinutes = timeLeftMs / 60000;

  const submissionWarningThresholdMinutes = getRemainingTimeWarningThresholdMinutes(claimMinutes);
  const warningTrigger = submissionWarningThresholdMinutes
    ? timeLeftMinutes <= submissionWarningThresholdMinutes
    : false;

  const hasShownToast = useRef(new Set<string>());

  // show toast when warning trigger is active
  const maybeShowExpiryWarning = useCallback(
    (timeLeftMinutes_: number) => {
      if (
        !taskId ||
        hasShownToast.current.has(taskId) ||
        !warningTrigger ||
        timeLeftMinutes_ <= 0
      ) {
        // already shown or not yet time to show
        return;
      }

      const timeLeftString =
        timeLeftMinutes_ < 1 ? `less than 1` : `${Math.round(timeLeftMinutes_)}`;

      // show the alert
      toast(
        <div className="flex">
          <p className="font-medium text-xs">
            <strong>Task will expire in {timeLeftString} min</strong>
            <br />
            To receive payment, submit before time runs out. Submissions will not be accepted after
            this time.
          </p>
        </div>,
        {
          position: 'top-left',
          autoClose: 10000,
          theme: 'dark',
        },
      );
      // mark as shown for this taskId
      hasShownToast.current.add(taskId);
    },
    [taskId, warningTrigger],
  );

  // time progress update interval
  useEffect(() => {
    const interval = setInterval(() => {
      const newTimeLeftMs = maxClaimDate.valueOf() - new Date().valueOf();
      setTimeLeftMs(newTimeLeftMs);

      const newTimeLeftMinutes = newTimeLeftMs / 60000;
      maybeShowExpiryWarning(newTimeLeftMinutes);
    }, 1000);

    return () => clearInterval(interval);
  }, [maxClaimDate, maybeShowExpiryWarning]);

  if (!claim) {
    return null;
  }

  if (!newHeader) {
    return (
      <TaskHeader.InfoHandlerItem visible={visible} title="Deadline">
        <div className="flex items-start">{deadlineStr}</div>
      </TaskHeader.InfoHandlerItem>
    );
  }

  return (
    <div className="">
      <Tooltip
        delayDuration={0}
        text={
          <ToolTipText
            text={
              <div>
                <div className="mb-2">
                  Remaining submission deadline:{' '}
                  <span className="font-semibold">
                    {formatTime(remainingDeadlineSeconds, TIME_FORMAT_PRESETS.REMAINING_TIME)}
                  </span>
                </div>
                <div
                  className="border border-red-900 p-1 rounded leading-relaxed"
                  style={{ backgroundColor: '#3B1219' }}
                >
                  To receive payment, submit before the task claim expiration on {deadlineStr}.
                  Submissions will not be accepted after this time.
                </div>
              </div>
            }
            width={300}
          />
        }
        target={
          <div
            className={`flex gap-1 items-center font-normal text-xs ${
              warningTrigger ? `text-danger-500` : 'text-neutral-700'
            }`}
          >
            <FontAwesomeIcon
              icon={['fas', `${warningTrigger ? 'triangle-exclamation' : 'calendar-clock'}`]}
            />
            Submit by {deadlineStr}
          </div>
        }
      />
    </div>
  );
};
